import { useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Check, X } from 'lucide-react'

interface ToastProps {
  message: string
  type?: 'success' | 'error'
  isVisible: boolean
  onClose: () => void
  duration?: number
}

export default function Toast({
  message,
  type = 'success',
  isVisible,
  onClose,
  duration = 2000
}: ToastProps) {
  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(() => {
        onClose()
      }, duration)

      return () => clearTimeout(timer)
    }
  }, [isVisible, duration, onClose])

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 400, damping: 28 }}
          className="fixed bottom-28 left-4 right-4 z-[60]"
        >
          <div
            className={`flex items-center justify-center gap-3 px-5 py-3 rounded-2xl shadow-lg border-2 ${
              type === 'success'
                ? 'bg-tg-secondary-bg border-green-500/40 text-tg-text'
                : 'bg-tg-secondary-bg border-red-500/40 text-tg-text'
            }`}
          >
            <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
              type === 'success' ? 'bg-green-500' : 'bg-red-500'
            }`}>
              {type === 'success' ? (
                <Check className="w-4 h-4 text-white" strokeWidth={3} />
              ) : (
                <X className="w-4 h-4 text-white" strokeWidth={3} />
              )}
            </div>
            <span className="text-sm font-medium">{message}</span>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
